import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import WebApp from "@twa-dev/sdk";
import { useTranslation, Trans } from 'react-i18next';
import { apiService } from '../services/api';
import useUserStore from '../stores/userStore';
import useInventoryStore from '../stores/inventoryStore';
import useCasesStore from '../stores/casesStore';
import { getCachedImage } from '../utils/imagePreloader';
import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import CurrencyIcon from './CurrencyIcon';

const API_URL = process.env.REACT_APP_API_URL;

const RightButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Button = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text.primary};
  font-size: 24px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const getImageUrl = (url) => {
  if (!url) return '';

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return url.startsWith('/') ? `${API_URL}${url}` : `${API_URL}/${url}`;
};

// Фиксированная высота предмета
const ITEM_HEIGHT = 300;

const WINNER_INDEX = 1500;
const TOTAL_COUNT = 3000;

const Header = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  z-index: 1000;
  background: linear-gradient(to bottom, rgba(0,0,0,0.8), transparent);
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Стили для модального окна
const ModalOverlay = styled.div`
  position: fixed;
  left: 0; top: 0; right: 0; bottom: 0;
  z-index: 2000;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalWindow = styled.div`
  background: ${({ theme }) => theme.card.background};
  border-radius: 20px;
  padding: 36px 28px 24px 28px;
  min-width: 320px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  text-align: center;
  max-width: 90vw;
`;

const ModalTitle = styled.div`
  font-weight: 700;
  font-size: 22px;
  margin-bottom: 14px;
  color: #ff4444;
`;

const ModalDesc = styled.div`
  color: ${({ theme }) => theme.text.secondary};
  font-size: 16px;
  margin-bottom: 28px;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  margin-bottom: 18px;
`;

const ModalButton = styled.button`
  flex: 1;
  background: ${({ primary }) => (primary ? '#007AFF' : '#fff')};
  color: ${({ primary }) => (primary ? '#fff' : '#007AFF')};
  border: ${({ primary }) => (primary ? 'none' : '1.5px solid #007AFF')};
  border-radius: 10px;
  padding: 12px 0;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
  &:hover {
    background: ${({ primary }) => (primary ? '#0056b3' : '#f0f4ff')};
    color: ${({ primary }) => (primary ? '#fff' : '#0056b3')};
  }
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.text.secondary};
  font-size: 15px;
  cursor: pointer;
  margin-top: 2px;
  opacity: 0.7;
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

// Функция для парсинга marketHashName
function parseMarketHashName(marketHashName) {
  if (!marketHashName) return { name: '', desc: '' };
  const [name, ...rest] = marketHashName.split('|');
  return {
    name: name.trim(),
    desc: rest.join('|').trim()
  };
}

const MainContainer = styled.div`
  height: 100vh;
  width: 100vw;
  background: #23272f;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 8px;
  max-width: 100vw;
  box-sizing: border-box;
  /* Скрываем скроллбар */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CaseView = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const virtuosoRef = React.useRef(null);

  const [currentCase, setCurrentCase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpening, setIsOpening] = useState(false);
  const [winningWeapon, setWinningWeapon] = useState(null);
  const [animationEnded, setAnimationEnded] = useState(false);
  const [randomizedItems, setRandomizedItems] = useState([]);
  const [showNoTokensModal, setShowNoTokensModal] = useState(false);
  const [centerIndex, setCenterIndex] = useState(500);

  const user = useUserStore(state => state.user);
  const setUser = useUserStore.setState;
  const balance = user?.balance ? parseFloat(user.balance) : 0;
  const { addItem } = useInventoryStore();
  // const { play, isSoundEnabled, toggleSound } = useSoundContext();
  const cases = useCasesStore(state => state.cases);
  const fetchCases = useCasesStore(state => state.fetchCases);
  const imagesLoaded = useCasesStore(state => state.imagesLoaded);
  const fetchItems = useInventoryStore(state => state.fetchItems);

  const lastIndexRef = React.useRef(null);

  useEffect(() => {
    if (WebApp.platform !== 'unknown') {
      WebApp.BackButton.show();
      WebApp.BackButton.onClick(() => {
        navigate('/cases', { replace: true });
      });
    }

    return () => {
      if (WebApp.platform !== 'unknown') {
        WebApp.BackButton.hide();
        WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  const handleRangeChanged = ({ startIndex, endIndex }) => {
    if (lastIndexRef.current !== null && lastIndexRef.current !== startIndex) {
      if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }

      // Скрываем кнопку "назад" во время прокрутки
      if (WebApp.platform !== 'unknown' && !isOpening) {
        WebApp.BackButton.hide();
        // Показываем кнопку обратно через небольшую задержку после остановки прокрутки
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          if (!isOpening) {
            WebApp.BackButton.show();
            WebApp.BackButton.onClick(() => {
              navigate('/cases', { replace: true });
            });
          }
        }, 300);
      }
    }
    lastIndexRef.current = startIndex;

    if (typeof startIndex === 'number' && typeof endIndex === 'number') {
      const visibleItems = endIndex - startIndex;
      const centerOffset = Math.floor(visibleItems / 2);
      setCenterIndex(startIndex + centerOffset);
    }
  };

  useEffect(() => {
    const fetchCase = async () => {
      // Сначала проверяем кейс в сторе
      const caseFromStore = cases.find(c => c.id === caseId);
      if (caseFromStore) {
        setCurrentCase(caseFromStore);
        const items = caseFromStore?.items || [];
        const shuffled = [...items].sort(() => Math.random() - 0.5);
        setRandomizedItems(shuffled);
        setIsLoading(false);
        return;
      }

      // Если кейса нет в сторе, пробуем загрузить все кейсы
      if (cases.length === 0) {
        await fetchCases();
        const updatedCase = cases.find(c => c.id === caseId);
        if (updatedCase) {
          setCurrentCase(updatedCase);
          const items = updatedCase?.items || [];
          const shuffled = [...items].sort(() => Math.random() - 0.5);
          setRandomizedItems(shuffled);
          setIsLoading(false);
          return;
        }
      }

      // Если кейс все еще не найден, делаем прямой запрос
      try {
        const response = await apiService.getCaseById(caseId);
        const caseData = response?.data?.data || response?.data;
        setCurrentCase(caseData);
        const items = caseData?.items || [];
        const shuffled = [...items].sort(() => Math.random() - 0.5);
        setRandomizedItems(shuffled);
      } catch {
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCase();
  }, [caseId, navigate, cases, fetchCases]);

  // Функция для приглашения друга
  const handleShareReferral = () => {
    const botUsername = process.env.REACT_APP_BOT_USERNAME || '';
    const refLink = `https://t.me/${botUsername}?startapp=ref_${user?.ref || ''}`;
    if (WebApp.isVersionAtLeast && WebApp.isVersionAtLeast('6.0')) {
      WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent('Присоединяйся!')}`);
    } else {
      navigator.clipboard.writeText(refLink);
      // Можно добавить toast
    }
  };

  const handleOpen = async () => {
    if (isOpening || !currentCase) return;
    if (balance < currentCase.tokens) {
      setShowNoTokensModal(true);
      return;
    }
    setIsOpening(true);
    if (WebApp.platform !== 'unknown') {
      WebApp.BackButton.hide();
    }

    // Очищаем историю навигации
    window.history.pushState(null, '', window.location.href);
    window.history.replaceState(null, '', window.location.href);

    try {
      let telegramId = WebApp.initDataUnsafe?.user?.id;
      const response = await apiService.openCase(currentCase.id, telegramId);
      if (!response.success) throw new Error(response.error || 'Ошибка при открытии кейса');
      const selectedWeapon = response.item;
      addItem(selectedWeapon);
      setWinningWeapon(selectedWeapon);
      // play('open');

      // Обновляем данные пользователя с бэка
      const me = await apiService.getMe();
      if (me.status === 'success' && me.data) {
        setUser({ user: me.data });
      }

      setTimeout(() => {
        if (virtuosoRef.current) {
          virtuosoRef.current.scrollToIndex({
            index: WINNER_INDEX,
            align: 'center',
            behavior: 'smooth'
          });
        }
        setTimeout(() => {
          setIsOpening(false);
          setAnimationEnded(true);
          if (WebApp.platform !== 'unknown') {
            WebApp.BackButton.show();
            WebApp.BackButton.onClick(() => {
              navigate('/cases', { replace: true });
            });
          }
          setTimeout(() => {
            navigate(`/case-result/${selectedWeapon.id}`, {
              state: {
                weapon: selectedWeapon,
                caseId: currentCase.id,
                caseCost: currentCase.tokens
              },
              replace: true
            });
          }, 2000);
        }, 1500);
      }, 500);
    } catch (error) {
      setUser(prev => ({ user: { ...prev.user, balance: (parseFloat(prev.user.balance) || 0) + (currentCase.tokens || 0) } }));
      setIsOpening(false);
      setWinningWeapon(null);
      setAnimationEnded(false);
      if (WebApp.platform !== 'unknown') {
        WebApp.BackButton.show();
        WebApp.BackButton.onClick(() => {
          navigate('/cases', { replace: true });
        });
      }
    }
  };

  if (isLoading) return <div>Загрузка...</div>;
  if (!currentCase) return <div>Ошибка</div>;

  const itemsArr = currentCase?.items || currentCase?.weapons || [];

  return (
    <MainContainer>
      <Header>
        {/* <CloseButton onClick={handleClose}>
        <IoClose />
      </CloseButton> */}
      </Header>
      <div style={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        overflowX: 'hidden',
        maxWidth: '100vw',
        boxSizing: 'border-box',
      }}>
        <Virtuoso
          ref={virtuosoRef}
          style={{ width: '100%', height: '100%', overflowX: 'hidden', maxWidth: '100vw' }}
          totalCount={TOTAL_COUNT}
          initialTopMostItemIndex={500}
          alignToBottom={false}
          followOutput={'auto'}
          itemContent={idx => {
            let weapon;
            if (idx === WINNER_INDEX && winningWeapon) {
              weapon = winningWeapon;
            } else {
              const itemsLength = randomizedItems.length;
              const normalizedIndex = ((idx % itemsLength) + itemsLength) % itemsLength;
              weapon = randomizedItems[normalizedIndex];
            }

            const cachedImage = getCachedImage(weapon.iconUrl);
            const imageStyle = {
              width: '100%',
              maxWidth: '100%',
              borderRadius: 8,
              opacity: cachedImage ? 1 : 0.5,
              transition: 'opacity 0.3s ease',
              objectFit: 'contain',
              boxSizing: 'border-box'
            };

            const isCenter = idx === centerIndex + 1;
            // Выделяем только выпавший элемент
            return (
              <div
                style={{
                  width: '100%',
                  background: idx === WINNER_INDEX && animationEnded ? '#4b69ff22' : 'none',
                  transition: 'background 0.3s, transform 0.3s',
                  display: 'flex',
                  justifyContent: 'center',
                  // marginBottom: 36,
                  transform: isCenter ? 'scale(1.15)' : 'scale(1)',
                  maxWidth: '100vw',
                  minWidth: 0
                }}
              >
                <div
                  style={{
                    height: 300,
                    width: '100%',
                    maxWidth: 300,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 30px',
                  }}
                >
                  <img
                    src={weapon.iconUrl}
                    alt={weapon.marketHashName}
                    style={imageStyle}
                    onLoad={e => { e.target.style.opacity = 1; }}
                  />
                  {(() => {
                    const { name, desc } = parseMarketHashName(weapon.marketHashName);
                    return (
                      <>
                        <div style={{
                          fontWeight: 700,
                          fontSize: 18,
                          color: '#fff',
                          marginTop: 12,
                          textAlign: 'center',
                          width: '100%'
                        }}>{typeof name === 'string' ? name : ''}</div>
                        <div style={{
                          fontSize: 15,
                          color: '#ccc',
                          marginTop: 2,
                          textAlign: 'center',
                          wordBreak: 'break-word',
                          width: '100%'
                        }}>{typeof desc === 'string' ? desc : ''}</div>
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          }}
          rangeChanged={handleRangeChanged}
        />
      </div>
      <div style={{ padding: '0 16px 24px 16px', background: 'transparent' }}>
        <button
          onClick={handleOpen}
          disabled={isOpening || !currentCase}
          style={{
            width: '100%',
            // maxWidth: 420,
            height: 56,
            borderRadius: 16,
            background: 'rgba(60,60,60,0.5)',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: 22,
            border: 'none',
            cursor: isOpening ? 'not-allowed' : 'pointer',
            opacity: isOpening ? 0.7 : 1,
            transition: 'all 0.2s',
            boxShadow: '0 2px 16px 0 rgba(0,0,0,0.10)',
            marginBottom: 8,
            marginTop: 32,
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block'
          }}
        >
          {isOpening ? t('case.opening') : (
            <Trans
              i18nKey="case.openFor"
              values={{ price: currentCase?.tokens }}
              components={{ currency: <CurrencyIcon size={16} /> }}
            />
          )}
        </button>
      </div>
      {/* Модалка если не хватает токенов */}
      {showNoTokensModal && (
        <ModalOverlay>
          <ModalWindow>
            <ModalTitle>
              <Trans
                i18nKey="common.notEnoughTokens"
                // values={{ needed: currentCase?.tokens || 0 }}
                components={{ currency: <CurrencyIcon size={16} /> }}
              />
            </ModalTitle>
            <ModalDesc>{t('case.notEnoughTokensDesc')}</ModalDesc>
            <ModalButtons>
              <ModalButton
                primary
                onClick={() => { setShowNoTokensModal(false); navigate('/packages'); }}
              >
                {t('case.buyTokens')}
              </ModalButton>
              <ModalButton
                onClick={() => { setShowNoTokensModal(false); handleShareReferral(); }}
              >
                {t('case.inviteFriend')}
              </ModalButton>
            </ModalButtons>
            <ModalClose onClick={() => setShowNoTokensModal(false)}>
              {t('case.close')}
            </ModalClose>
          </ModalWindow>
        </ModalOverlay>
      )}
      {/* <button
        onClick={toggleSound}
        disabled={isOpening}
        style={{
          position: 'fixed',
          bottom: 80,
          right: 20,
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: '#222',
          color: '#fff',
          border: 'none',
          fontSize: 24,
          cursor: isOpening ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        {isSoundEnabled ? <IoVolumeMedium /> : <IoVolumeMute />}
      </button> */}
      {isOpening && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.05)',
          zIndex: 9999,
          pointerEvents: 'all',
          cursor: 'not-allowed'
        }} />
      )}
    </MainContainer>
  );
};

const Container = styled.div`
height: 100vh;
display: flex;
flex-direction: column;
background: ${props => props.theme.background};
${'' /* padding-top: 50px; */}
position: relative;
`;

const SoundButton = styled.button`
position: fixed;
bottom: 100px;
right: 20px;
background: ${props => props.theme.card.background};
border: none;
border-radius: 50%;
width: 48px;
height: 48px;
display: flex;
align-items: center;
justify-content: center;
color: ${props => props.theme.text.primary};
cursor: pointer;
z-index: 1000;
box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
transition: all 0.2s ease;

&:hover:not(:disabled) {
  transform: scale(1.05);
}

&:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
`;

const LoadingText = styled.div`
text-align: center;
color: ${props => props.theme.text.primary};
font-size: 18px;
margin-top: 40px;
`;

const ErrorText = styled(LoadingText)`
`;

const VerticalContainer = styled.div`
height: 100vh;
width: 100vw;
background: ${props => props.theme.background};
display: flex;
flex-direction: column;
position: relative;
overflow: hidden;
padding-top: 50px;
`;
const VerticalRouletteScreen = styled.div`
flex: 1;
display: flex;
align-items: stretch;
justify-content: center;
height: 100vh;
width: 100vw;
position: relative;
overflow: hidden;
`;
const VerticalRouletteModal = styled.div`
width: 100vw;
height: 100vh;
max-width: 100vw;
max-height: 100vh;
position: relative;
background: transparent;
border-radius: 0;
overflow: hidden;
display: flex;
align-items: center;
justify-content: center;
`;

// Градиентные оверлеи сверху и снизу
const GradientOverlay = styled.div`
position: absolute;
left: 0; right: 0;
height: 80px;
z-index: 10;
pointer-events: none;
background: linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%);
`;
const GradientOverlayBottom = styled.div`
position: absolute;
left: 0; right: 0;
bottom: 0;
height: 80px;
z-index: 10;
pointer-events: none;
background: linear-gradient(0deg, rgba(0,0,0,0.7) 0%, transparent 100%);
`;

// Стрелки-указатели
const Arrow = styled.div`
position: absolute;
top: calc(50% - 24px);
z-index: 20;
color: rgba(255,255,255,0.15);
font-size: 48px;
pointer-events: none;
`;
const ArrowLeft = styled(Arrow)`
left: 16px;
`;
const ArrowRight = styled(Arrow)`
right: 16px;
transform: scaleX(-1);
`;

const VerticalRouletteItem = styled.div`
width: 400px;
max-width: 90vw;
height: min(400px, 50vh);
min-height: 220px;
margin: 56px 0;
display: flex;
flex-direction: column;
align-items: center;
background: none;
transition: transform 0.5s, box-shadow 0.5s;
${({ isWinning, theme }) => isWinning && `
  transform: scale(1.1);
  box-shadow: 0 0 32px 8px ${theme.primary};
  z-index: 2;
`}
`;
const VerticalRouletteItemImage = styled.div`
img {
  width: 220px;
  max-width: 80vw;
  border-radius: 8px;
  background: #222;
  box-shadow: 0 4px 24px rgba(0,0,0,0.4);
  ${({ isWinning, theme }) => isWinning && `
    box-shadow: 0 0 32px 8px ${theme.primary};
  `}
}
`;
const VerticalRouletteItemName = styled.div`
font-size: 18px;
font-weight: 700;
margin-top: 12px;
text-align: center;
color: ${props => props.theme.text.primary};
`;
const VerticalRouletteItemDesc = styled.div`
font-size: 14px;
opacity: 0.8;
text-align: center;
color: ${props => props.theme.text.secondary};
`;
const VerticalRouletteAction = styled.div`
position: fixed;
left: 0;
right: 0;
bottom: 0;
z-index: 10;
display: flex;
justify-content: center;
padding: 0 0 24px 0;
pointer-events: none;
`;
const VerticalRouletteButton = styled.button`
width: 100%;
background: ${props => props.theme.primary};
color: #fff;
font-weight: bold;
font-size: 16px;
border-radius: 12px;
border: none;
height: 48px;
padding: 16px;
display: flex;
align-items: center;
justify-content: center;
gap: 8px;
cursor: pointer;
box-sizing: border-box;
box-shadow: 0 2px 12px rgba(0,0,0,0.2);
transition: all 0.2s;
pointer-events: auto;
&:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-2px);
}
&:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
`;

export default CaseView;